'use client';

import React, { useEffect, useState } from 'react';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { ChevronUpIcon } from '@heroicons/react/20/solid';
import { twJoin } from 'tailwind-merge';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { ContentChunkFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { ArrayElementType } from '@/integrations/crystallize/types/helpers/ArrayElementType';
import { buildTrackingId } from '@/utils/tracking-helper';

interface FaqQuestionProps {
  question: ArrayElementType<ContentChunkFragment['chunks']>;
  trackingPrefix?: string;
}

export default function FaqQuestion({ question, trackingPrefix }: Readonly<FaqQuestionProps>) {
  const faq = extractComponentFromChunk(question, 'faq', 'ItemRelationsContent')?.items?.[0]
    ?.components;
  const faqId = extractComponentFromChunk(question, 'faq', 'ItemRelationsContent')?.items?.[0]?.id;
  const questionText =
    faq?.[0]?.content?.__typename === 'SingleLineContent' ? faq?.[0].content?.text : undefined;
  const answerText =
    faq?.[1]?.content?.__typename === 'RichTextContent' ? faq?.[1].content : undefined;
  const image = faq?.[2]?.content?.__typename === 'ImageContent' ? faq?.[2].content : undefined;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash === faqId) {
      setOpen(true);
    }
  }, [faqId]);

  if (!questionText || !answerText) {
    return null;
  }

  const answerId = faqId ? `answer-${faqId}` : undefined;
  const questionId = faqId ? `question-${faqId}` : undefined;

  const faqQuestionTrackingId = buildTrackingId(trackingPrefix, questionText);

  return (
    <div id={faqId}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={answerId}
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full justify-between gap-4 py-6 sm:py-8"
        data-tile-element-id={faqQuestionTrackingId}
      >
        <h3 id={questionId} className=" text-left text-md font-medium sm:text-lg lg:text-2xl">
          {questionText}
        </h3>
        <ChevronUpIcon
          className={twJoin(
            'h-auto w-6 flex-shrink-0 self-start text-primary-100 duration-200 ease-in sm:mr-2',
            !open && 'rotate-180 transform',
          )}
          aria-hidden="true"
        />
      </button>
      <section
        id={answerId}
        aria-labelledby={questionId}
        className={twJoin('mb-8 pr-8 sm:pr-16 sm:text-md', !open && 'hidden')}
      >
        <RichTextRenderer blocks={answerText} />
        <OptimizedImage
          imageComponent={image}
          sizes="600px"
          width={600}
          height={400}
          className="mx-auto max-w-[400px]"
        />
      </section>
    </div>
  );
}
